'use client';

import { useMemo, useContext } from 'react';
import { ProductsContext } from 'context-providers/products-provider';

import styled from 'styled-components';
import { mqLargeMin } from 'styled/tools/media-queries';
import type { BundleInfoModule } from 'lib/pdp-modules/types';

const BundleWrapper = styled.div`
  margin: 1rem auto;
  padding: 1rem;
  max-width: 82rem;
  width: 100%;
  display: flex;
  gap: 1rem;
  flex-direction: column;

  @media ${mqLargeMin} {
    flex-direction: row;
  }
`;

const BundleCard = styled.div`
  position: relative;
  flex: 1 0 0;
  border-radius: 1rem;
  padding: 1.5rem 1rem;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  align-items: center;
  background-color: var(--fill-secondary);

  * {
    color: var(--text-primary);
  }

  @media ${mqLargeMin} {
    border-radius: 2rem;
  }
`;

const BundleImage = styled.img`
  height: 100%;
  width: 100%;
  object-fit: cover;
`;

const BundleTitle = styled.p`
  margin-bottom: 0.5rem;
  font-size: 20px;
  font-weight: 350;
  line-height: 120%;
  letter-spacing: -0.36px;

  @media ${mqLargeMin} {
    margin: 0.5rem 0 1rem;
    font-size: 24px;
  }
`;

const CartContent = styled.p`
  font-size: 16px;
  font-weight: 350;
  line-height: 140%;
  letter-spacing: -0.16px;
`;

const Tag = styled.span`
  border: 1px solid var(--outline-primary);
  border-radius: 10rem;
  padding: 0.375rem;
  height: 1.25rem;
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 350;
  line-height: 100%;
  letter-spacing: -0.24px;
`;

const CardLink = styled.a`
  display: inline-block;
  margin-top: 1rem;
  font-weight: 500;
  line-height: normal;
  letter-spacing: -0.04px;
  text-underline-offset: 0.5rem;

  @media ${mqLargeMin} {
    margin-top: 1.5rem;
  }
`;

const MobileOnly = styled.span`
  @media ${mqLargeMin} {
    display: none;
  }
`;

const DesktopOnly = styled.span`
  display: none;

  @media ${mqLargeMin} {
    display: inline;
  }
`;

const Badge = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  border-radius: 16px;
  padding: 6px;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 1;
  background-color: var(--guidance-primary);
  color: var(--text-primary);

  &.dm-wk {
    background-color: var(--products-dm-02-dark);
    color: var(--text-inverse-primary);
  }
`;

type ProductBundleItemsProps = {
  selectedSkus: string[];
  bundleProducts: BundleInfoModule;
};

export default function ProductBundleItems({
  selectedSkus,
  bundleProducts,
}: ProductBundleItemsProps) {
  const { getProduct } = useContext(ProductsContext);

  const selectedProduct = getProduct(selectedSkus[0]);
  const bundleProduct = getProduct(selectedSkus[1]);

  const getProductTitleParts = (name) => {
    const splitIndex = name.match(/(™|®)/)?.index || -1;
    return {
      tag: splitIndex >= 0 ? name.slice(0, splitIndex + 1) : false,
      title: splitIndex >= 0 ? name.slice(splitIndex + 1) : name,
    };
  };

  const selectedProductTitleInfo = useMemo(() => {
    return getProductTitleParts(selectedProduct.name);
  }, [selectedSkus]);

  const bundleProductTitleInfo = useMemo(() => {
    return getProductTitleParts(bundleProduct.name);
  }, [selectedSkus]);

  const [productOne, productTwo] = bundleProducts.products;

  const bundleCardOneDesktopContent = productOne.cardContent;
  const bundleCardOneMobileContent =
    productOne.cardMobileContent || bundleCardOneDesktopContent;

  const bundleCardTwoDesktopContent = productTwo.cardContent;
  const bundleCardTwoMobileContent =
    productTwo.cardMobileContent || bundleCardTwoDesktopContent;

  return (
    <BundleWrapper>
      <BundleCard>
        {selectedProduct?.shopifyData?.tags && (
          <Badge className={selectedProduct.shopifyData.sku}>
            {selectedProduct.shopifyData.tags[0]}
          </Badge>
        )}
        <BundleImage src={productOne.image?.url} alt={productOne.image?.alt} />
        <div>
          {selectedProductTitleInfo.tag && (
            <Tag>{selectedProductTitleInfo.tag}</Tag>
          )}
          <BundleTitle>{selectedProductTitleInfo.title}</BundleTitle>
          <CartContent>
            <MobileOnly>{bundleCardOneMobileContent}</MobileOnly>
            <DesktopOnly>{bundleCardOneDesktopContent}</DesktopOnly>
          </CartContent>
          {productOne.cardLink && (
            <CardLink href={productOne.cardLink}>Learn More</CardLink>
          )}
        </div>
      </BundleCard>
      <BundleCard>
        {bundleProduct?.shopifyData?.tags && (
          <Badge className={bundleProduct.shopifyData.sku}>
            {bundleProduct.shopifyData.tags[0]}
          </Badge>
        )}
        <BundleImage src={productTwo.image?.url} alt={productTwo.image?.alt} />
        <div>
          {bundleProductTitleInfo.tag && (
            <Tag>{bundleProductTitleInfo.tag}</Tag>
          )}
          <BundleTitle>{bundleProductTitleInfo.title}</BundleTitle>
          <CartContent>
            <MobileOnly>{bundleCardTwoMobileContent}</MobileOnly>
            <DesktopOnly>{bundleCardTwoDesktopContent}</DesktopOnly>
          </CartContent>
          {productTwo.cardLink && (
            <CardLink href={productTwo.cardLink}>Learn More</CardLink>
          )}
        </div>
      </BundleCard>
    </BundleWrapper>
  );
}
